import { Placeholder } from '@tiptap/extension-placeholder';
import { NodeSelection } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';
import { type Editor, type JSONContent, useEditor, useEditorState } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { type KeyboardEvent, type MouseEvent, type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  useTrackAgentComposerSkillAdded,
  useTrackAgentTaskSubmitted,
  useTrackAgentThreadOpened,
  type WorkspaceAgentComposerSkillInsertSource,
  type WorkspaceAgentGuidedFlowSubmissionParams,
  type WorkspaceAgentTaskSubmittedType,
  type WorkspaceAgentThreadCreatedLocation,
} from '@air/analytics';
import {
  hydrateSelectedItemsAction,
  resetSelectedItemsAction,
  type SelectableItem,
  selectedItemsSelector,
} from '@air/redux-selected-items';
import { type AgentInboundMessagePill } from '@air/server-sdk';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import {
  type WorkspaceAgentMentionPopupRef,
  type WorkspaceAgentMentionPopupSessionProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionPopup';
import {
  type WorkspaceAgentSkillTriggerMenuPopupRef,
  type WorkspaceAgentSkillTriggerMenuSessionProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/skills/WorkspaceAgentSkillTriggerMenu';
import { WORKSPACE_AGENT_PILL_TRIGGER_ATTRIBUTE } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPillTriggerAttribute';
import { isWorkspaceAgentComposerSelectionDragDataTransfer } from '~/components/v2/components/workspace/workspace-agent-panel/drag/workspaceAgentComposerSelectionDragPayload';
import { useIsActiveWorkspaceAgentSessionArchived } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsActiveWorkspaceAgentSessionArchived';
import { usePrivateWorkspaceAgentMarketingComposerDraftSync } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentMarketingComposerDraftSync';
import { usePrivateWorkspaceAgentSkillsCatalogPagination } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentSkillsCatalogPagination';
import { usePrivateWorkspaceAgentVisualContext } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentVisualContext';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import { usePrivateWorkspaceAgentMentionSuggestionState } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/usePrivateWorkspaceAgentMentionSuggestionState';
import { WorkspaceAgentMentionSuggestion } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionSuggestion';
import {
  usePrivateWorkspaceAgentPanelComposerPills,
  usePrivateWorkspaceAgentPanelSession,
} from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { usePrivateWorkspaceAgentSkillsCatalog } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentSkillsCatalogProvider';
import { usePrivateWorkspaceAgentSkillSuggestionState } from '~/components/v2/components/workspace/workspace-agent-panel/slash-commands/usePrivateWorkspaceAgentSkillSuggestionState';
import { useWorkspaceAgentSkillMonikerCatalogUpgradeSync } from '~/components/v2/components/workspace/workspace-agent-panel/slash-commands/useWorkspaceAgentSkillMonikerCatalogUpgradeSync';
import { WorkspaceAgentSkillSuggestion } from '~/components/v2/components/workspace/workspace-agent-panel/slash-commands/workspaceAgentSkillSuggestion';
import {
  findWorkspaceAgentPillIdByReference,
  type WorkspaceAgentPillReference,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/findWorkspaceAgentPillIdByReference';
import {
  WORKSPACE_AGENT_PILL_NODE_NAME,
  WorkspaceAgentPillExtension,
  type WorkspaceAgentPillNodeAttrs,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';
import { WorkspaceAgentSkillMonikerNode } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentSkillMoniker';
import { type WorkspaceAgentStructuredDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentStructuredDisplay';
import { useUploadedBatchOfferStore } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';
import { buildAgentTaskSubmittedParams } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildAgentTaskSubmittedParams';
import {
  buildWorkspaceAgentComposerOutboundPills,
  normalizeWorkspaceAgentOutboundPills,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentComposerOutboundPills';
import { buildWorkspaceAgentComposerSeedContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentComposerSeedContent';
import { buildWorkspaceAgentOutboundMetadata } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentOutboundMetadata';
import { collectWorkspaceAgentComposerSkillIds } from '~/components/v2/components/workspace/workspace-agent-panel/utils/collectWorkspaceAgentComposerSkillIds';
import { getWorkspaceAgentComposerStopTooltip } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentComposerStopTooltip';
import { hasSendableWorkspaceAgentComposerContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/hasSendableWorkspaceAgentComposerContent';
import { insertWorkspaceAgentSkillMonikerInEditor } from '~/components/v2/components/workspace/workspace-agent-panel/utils/insertWorkspaceAgentSkillMonikerInEditor';
import { parseWorkspaceAgentComposerClipboardText } from '~/components/v2/components/workspace/workspace-agent-panel/utils/parseWorkspaceAgentComposerClipboardText';
import {
  parseWorkspaceAgentComposerReferenceUrl,
  resolveWorkspaceAgentComposerReferenceUrl,
  splitWorkspaceAgentComposerTextByReferenceUrls,
  workspaceAgentComposerTextContainsReferenceUrl,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/parseWorkspaceAgentComposerReferenceUrl';
import { serializeWorkspaceAgentComposerContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/serializeWorkspaceAgentComposerContent';
import { shouldInsertWorkspaceAgentComposerPlainTextPaste } from '~/components/v2/components/workspace/workspace-agent-panel/utils/shouldInsertWorkspaceAgentComposerPlainTextPaste';
import { splitWorkspaceAgentOutboundListPills } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitWorkspaceAgentOutboundListPills';
import { useWorkspaceAgentComposerLock } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerLockProvider';
import { useWorkspaceAgentComposerSeed } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { type Skill } from '~/hooks/queries/skills/usePrivateSkillsList';
import { useAirStore } from '~/utils/ReduxUtils';

const COMPOSER_PLACEHOLDER = 'Ask Air anything, drop an image, or type @ to add a board or asset…';

const COMPOSER_PLACEHOLDER_WITH_SKILLS = 'Ask Air anything, drop an image, type @ for a reference, or / for a skill…';

const blockProseMirrorAgentSelectionDrop = (view: EditorView, event: DragEvent): boolean => {
  if (!isWorkspaceAgentComposerSelectionDragDataTransfer(event.dataTransfer)) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();
  view.dom.blur();
  return true;
};

const workspaceAgentComposerSelectionDropEditorDomEventHandlers = {
  dragenter: blockProseMirrorAgentSelectionDrop,
  dragover: blockProseMirrorAgentSelectionDrop,
  drop: blockProseMirrorAgentSelectionDrop,
};

/** The node-selected chip's own DOM, if a chip is what the selection is: the pill is an atom, so it is selected whole. */
const getSelectedWorkspaceAgentPillElement = (view: EditorView): HTMLElement | null => {
  const { selection } = view.state;

  if (!(selection instanceof NodeSelection) || selection.node.type.name !== WORKSPACE_AGENT_PILL_NODE_NAME) {
    return null;
  }

  const nodeDom = view.nodeDOM(selection.from);

  return nodeDom instanceof HTMLElement ? nodeDom : null;
};

/**
 * Chips are out of the tab order, so with a chip node-selected Enter/Space presses its trigger instead
 * of sending. The marker is the panel's own, set by `WorkspaceAgentReferencePill`: a primitive's ARIA
 * moves when the primitive is swapped, and the remove button in the same node would answer to it.
 */
const getWorkspaceAgentPillTrigger = (pillElement: HTMLElement): HTMLElement | null =>
  pillElement.querySelector<HTMLElement>(`[${WORKSPACE_AGENT_PILL_TRIGGER_ATTRIBUTE}]`);

/** Updated each render from the composer hook so the hoisted selector can resolve skills for send gating. */
const workspaceAgentComposerResolveSkillRef: {
  current: ((skillId: string) => Skill | undefined) | undefined;
} = { current: undefined };

/** Hoisted so the subscription keeps one selector: a fresh one per render re-runs the doc walk. */
const selectHasSendableWorkspaceAgentComposerContent = ({ editor }: { editor: Editor | null }): boolean =>
  !!editor &&
  hasSendableWorkspaceAgentComposerContent(
    editor,
    workspaceAgentComposerResolveSkillRef.current
      ? { resolveSkill: workspaceAgentComposerResolveSkillRef.current }
      : undefined,
  );

const COMPOSER_ARIA_LABEL = 'Message the workspace agent';

type WorkspaceAgentComposerEditorKeyDownEvent = Parameters<WorkspaceAgentMentionPopupRef['onKeyDown']>[0];

type WorkspaceAgentComposerEditorKeyDown = (
  view: EditorView,
  event: WorkspaceAgentComposerEditorKeyDownEvent,
) => boolean;

type WorkspaceAgentComposerEditorKeyDownParams = {
  canSubmit: boolean;
  event: WorkspaceAgentComposerEditorKeyDownEvent;
  onMentionPopupKeyDown: WorkspaceAgentMentionPopupRef['onKeyDown'];
  onSkillPopupKeyDown: WorkspaceAgentSkillTriggerMenuPopupRef['onKeyDown'];
  onSubmit: () => void;
  view: EditorView;
};

// ProseMirror is the typeable node; TipTap's EditorContent wrapper is not contenteditable.
// Idle combobox attrs. Live `aria-expanded` / `aria-controls` / `aria-haspopup` overlay via
// `editorAriaAttributes` on `editorOptions` — `useEditor` re-applies that object every render.
const WORKSPACE_AGENT_COMPOSER_EDITOR_ATTRIBUTES = {
  'aria-autocomplete': 'list',
  // Assets is the default source (`listbox`). Boards overlays `tree` via live `editorAriaAttributes`.
  'aria-haspopup': 'listbox',
  'aria-label': COMPOSER_ARIA_LABEL,
  'data-testid': 'WORKSPACE_AGENT_COMPOSER_EDITOR_INPUT',
  role: 'combobox',
};

const handleWorkspaceAgentComposerEditorKeyDown = ({
  canSubmit,
  event,
  onMentionPopupKeyDown,
  onSkillPopupKeyDown,
  onSubmit,
  view,
}: WorkspaceAgentComposerEditorKeyDownParams): boolean => {
  // A chord is the editor's, never the picker's: Shift+Enter is a newline and Cmd/Ctrl/Alt
  // combinations are shortcuts, even with the picker open.
  const hasModifier = event.shiftKey || event.metaKey || event.ctrlKey || event.altKey;

  // The open picker owns Enter/Tab/Escape/Arrows. `editorProps` runs before plugin props, so
  // this precedes both the suggestion plugin and Enter-to-send.
  if (!hasModifier && onSkillPopupKeyDown(event)) {
    event.preventDefault();

    return true;
  }

  if (!hasModifier && onMentionPopupKeyDown(event)) {
    event.preventDefault();

    return true;
  }

  const selectedPillElement =
    event.key === 'Enter' || event.key === ' ' ? getSelectedWorkspaceAgentPillElement(view) : null;

  if (selectedPillElement && !hasModifier) {
    const pillTrigger = getWorkspaceAgentPillTrigger(selectedPillElement);

    if (pillTrigger) {
      event.preventDefault();
      pillTrigger.click();

      return true;
    }
  }

  // ProseMirror would replace the selected atom with the space whatever modifier is held —
  // deleting the chip — so the key never reaches it. Enter still falls through to the send below.
  if (selectedPillElement && event.key === ' ') {
    event.preventDefault();

    return true;
  }

  if (event.key !== 'Enter' || event.shiftKey) {
    return false;
  }

  // Nothing to send, and plain Enter is the send key on this composer — left to ProseMirror it
  // opens paragraphs in an empty composer and pushes the placeholder down the box. Shift+Enter
  // is the newline, and it returned above.
  if (!canSubmit) {
    event.preventDefault();

    return true;
  }

  event.preventDefault();
  onSubmit();

  return true;
};

const pillReferenceFromAttrs = (attrs: WorkspaceAgentPillNodeAttrs): WorkspaceAgentPillReference | null => {
  if (attrs.type === 'board' && attrs.boardId) {
    return { boardId: attrs.boardId, type: 'board' };
  }

  if (attrs.type === 'asset' && attrs.assetId) {
    return { assetId: attrs.assetId, type: 'asset', versionId: attrs.versionId };
  }

  return null;
};

const insertWorkspaceAgentComposerPastedReferenceUrls = async ({
  editor,
  insertAt,
  text,
  workspaceId,
}: {
  editor: Editor;
  insertAt: { from: number; to: number };
  text: string;
  workspaceId: string;
}): Promise<boolean> => {
  if (editor.isDestroyed || !workspaceAgentComposerTextContainsReferenceUrl(text)) {
    return false;
  }

  const content: JSONContent[] = [];

  for (const segment of splitWorkspaceAgentComposerTextByReferenceUrls(text)) {
    if (segment.kind === 'text') {
      if (segment.text) {
        content.push({ text: segment.text, type: 'text' });
      }

      continue;
    }

    const reference = parseWorkspaceAgentComposerReferenceUrl(segment.url);

    if (!reference) {
      content.push({ text: segment.url, type: 'text' });
      continue;
    }

    const resolvedAttrs = await resolveWorkspaceAgentComposerReferenceUrl({ reference, workspaceId });

    if (!resolvedAttrs) {
      content.push({ text: segment.url, type: 'text' });
      continue;
    }

    const pillReference = pillReferenceFromAttrs(resolvedAttrs);
    const existingPillId =
      pillReference === null ? null : findWorkspaceAgentPillIdByReference(editor.state.doc, pillReference);

    content.push(
      {
        attrs: existingPillId ? { ...resolvedAttrs, id: existingPillId } : resolvedAttrs,
        type: WORKSPACE_AGENT_PILL_NODE_NAME,
      },
      { text: ' ', type: 'text' },
    );
  }

  if (editor.isDestroyed) {
    return false;
  }

  editor.chain().focus().insertContentAt(insertAt, content).scrollIntoView().run();

  const selection = window.getSelection();

  if (selection && selection.rangeCount > 0) {
    selection.collapseToEnd();
  }

  return true;
};

/**
 * Passing the original paste event re-enters this handler with the same clipboard HTML and loops.
 * `pasteText` without an event builds an empty ClipboardEvent, so `shouldInsert` returns false and
 * the inner `doPaste` applies the open slice from `clipboardTextParser`.
 */
const handleWorkspaceAgentComposerPlainTextPaste = (view: EditorView, event: ClipboardEvent): boolean => {
  const html = event.clipboardData?.getData('text/html');
  const text = event.clipboardData?.getData('text/plain');

  if (!shouldInsertWorkspaceAgentComposerPlainTextPaste({ html, text }) || !text) {
    return false;
  }

  view.pasteText(text);

  return true;
};

const handleWorkspaceAgentComposerPaste = ({
  editor,
  event,
  referenceUrlPasteInFlightRef,
  view,
  workspaceId,
}: {
  editor: Editor | null;
  event: ClipboardEvent;
  referenceUrlPasteInFlightRef: { current: boolean };
  view: EditorView;
  workspaceId: string | undefined;
}): boolean => {
  const html = event.clipboardData?.getData('text/html');
  const text = event.clipboardData?.getData('text/plain');

  if (html?.includes('data-workspace-agent-pill') || html?.includes('data-workspace-agent-skill-moniker')) {
    return false;
  }

  if (text && workspaceId && editor && !editor.isDestroyed && workspaceAgentComposerTextContainsReferenceUrl(text)) {
    if (referenceUrlPasteInFlightRef.current) {
      event.preventDefault();

      return true;
    }

    event.preventDefault();

    const insertAt = { from: editor.state.selection.from, to: editor.state.selection.to };
    referenceUrlPasteInFlightRef.current = true;

    void insertWorkspaceAgentComposerPastedReferenceUrls({ editor, insertAt, text, workspaceId })
      .then((didInsert) => {
        if (!didInsert && !editor.isDestroyed) {
          view.pasteText(text);
        }
      })
      .catch((error: unknown) => {
        reportErrorToBugsnag({
          context: 'insertWorkspaceAgentComposerPastedReferenceUrls',
          error: error instanceof Error ? error : new Error('Workspace agent composer reference paste failed'),
        });

        if (!editor.isDestroyed) {
          view.pasteText(text);
        }
      })
      .finally(() => {
        referenceUrlPasteInFlightRef.current = false;
      });

    return true;
  }

  return handleWorkspaceAgentComposerPlainTextPaste(view, event);
};

type SubmittedWorkspaceSelectionSnapshot = {
  includeNestedItems: boolean;
  selectAll: boolean;
  selectAllRootBoardId: string | undefined;
  selectedItems: SelectableItem[];
};

type PendingStructuredSend = {
  contentSnapshot: string;
  structuredDisplay: WorkspaceAgentStructuredDisplay;
};

export type UsePrivateWorkspaceAgentComposerEditorResult = {
  canSubmit: boolean;
  composerPlaceholder: string;
  composerRunStatusMessage: string;
  composerStopTooltip: string | undefined;
  editor: Editor | null;
  handleAddReference: () => void;
  handleInsertSkillFromActionsMenu: (skill: Skill) => void;
  handleSendKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  handleStop: () => void;
  handleStopKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  handleStopMouseDown: (event: MouseEvent<HTMLButtonElement>) => void;
  handleSubmit: () => void;
  isComposerLocked: boolean;
  isComposerRunLocked: boolean;
  isStopEnabled: boolean;
  isStopLoading: boolean;
  mentionPopupProps: WorkspaceAgentMentionPopupSessionProps | null;
  mentionPopupRef: RefObject<WorkspaceAgentMentionPopupRef>;
  refocusEditor: () => void;
  skillPopupProps: WorkspaceAgentSkillTriggerMenuSessionProps | null;
  skillPopupRef: RefObject<WorkspaceAgentSkillTriggerMenuPopupRef>;
};

/**
 * These buttons are `aria-disabled`, not `disabled`, so they keep their place in the tab order —
 * which means the keys that would activate them have to be swallowed by hand.
 */
const blockActivationWhen = (isDisabled: boolean) => (event: KeyboardEvent<HTMLButtonElement>) => {
  if (isDisabled && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault();
  }
};

export const usePrivateWorkspaceAgentComposerEditor = (): UsePrivateWorkspaceAgentComposerEditorResult => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { blockIfCreditsDepleted, isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
  const { isActiveSessionArchived, isActiveSessionArchiveStatusPending } = useIsActiveWorkspaceAgentSessionArchived();
  const isActiveSessionArchiveBlocked = isActiveSessionArchived || isActiveSessionArchiveStatusPending;
  const session = usePrivateWorkspaceAgentPanelSession();
  const { hasExplicitSelection, scopePills } = usePrivateWorkspaceAgentPanelComposerPills();
  const { visualContext } = usePrivateWorkspaceAgentVisualContext();
  const { clearSeed, seed } = useWorkspaceAgentComposerSeed();
  const { isComposerLocked } = useWorkspaceAgentComposerLock();
  const closeGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.closeGuidedVariantFlow);
  const { trackAgentComposerSkillAdded } = useTrackAgentComposerSkillAdded();
  const { trackAgentTaskSubmitted } = useTrackAgentTaskSubmitted();
  const { trackAgentThreadOpened } = useTrackAgentThreadOpened();

  const handleComposerSkillMonikerInserted = useCallback(
    (skill: Pick<Skill, 'id' | 'scope' | 'type'>, insertSource: WorkspaceAgentComposerSkillInsertSource) => {
      trackAgentComposerSkillAdded({
        insert_source: insertSource,
        scope: skill.scope === 'private' ? 'private' : 'workspace',
        skill_id: skill.id,
        type: skill.type,
      });
    },
    [trackAgentComposerSkillAdded],
  );
  const taskSubmitTypeRef = useRef<WorkspaceAgentTaskSubmittedType>('manual_submit');
  const actionsMenuTaskIdRef = useRef<string | undefined>(undefined);
  const guidedFlowSubmissionRef = useRef<WorkspaceAgentGuidedFlowSubmissionParams | undefined>(undefined);
  const pendingFocus = useWorkspaceAgentPanelStore((state) => state.pendingFocus);
  const clearPendingFocus = useWorkspaceAgentPanelStore((state) => state.clearPendingFocus);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);

  const handleSubmitRef = useRef<() => void>(() => undefined);
  const pendingStructuredSendRef = useRef<PendingStructuredSend | undefined>();
  const previousSessionIdForPendingStructuredSendRef = useRef(session.sessionId);
  const composerEditorKeyDownRef = useRef<WorkspaceAgentComposerEditorKeyDown>(() => false);
  const previousComposerRunLockedRef = useRef<boolean | null>(null);

  const { getSkillById, isWorkspaceAgentSlashSkillsEnabled, skills } = usePrivateWorkspaceAgentSkillsCatalog();
  const { hasSkillsListNextPage, requestSkillsListNextPage } = usePrivateWorkspaceAgentSkillsCatalogPagination();
  const skillsRef = useRef(skills);
  skillsRef.current = skills;
  const getSkillByIdRef = useRef(getSkillById);
  getSkillByIdRef.current = getSkillById;
  const hasSkillsListNextPageRef = useRef(hasSkillsListNextPage);
  hasSkillsListNextPageRef.current = hasSkillsListNextPage;
  const requestSkillsListNextPageRef = useRef(requestSkillsListNextPage);
  requestSkillsListNextPageRef.current = requestSkillsListNextPage;

  const composerSerializeOptions = useMemo(
    () => ({ resolveSkill: (skillId: string) => getSkillByIdRef.current(skillId) }),
    [],
  );

  workspaceAgentComposerResolveSkillRef.current = composerSerializeOptions.resolveSkill;

  const {
    editorAriaAttributes: mentionEditorAriaAttributes,
    openManualPicker,
    popupProps: mentionPopupProps,
    popupRef: mentionPopupRef,
    suggestionOptions,
  } = usePrivateWorkspaceAgentMentionSuggestionState({ isCreditsDepleted });

  const {
    editorAriaAttributes: skillEditorAriaAttributes,
    popupProps: skillPopupProps,
    popupRef: skillPopupRef,
    isSlashSkillsEnabled,
    skillSuggestionOptions,
  } = usePrivateWorkspaceAgentSkillSuggestionState({
    isCreditsDepleted,
    onSkillMonikerInserted: handleComposerSkillMonikerInserted,
  });

  const editorAriaAttributes = useMemo(() => {
    if (mentionPopupProps) {
      return mentionEditorAriaAttributes;
    }

    if (skillPopupProps) {
      return { ...mentionEditorAriaAttributes, ...skillEditorAriaAttributes };
    }

    return mentionEditorAriaAttributes;
  }, [mentionEditorAriaAttributes, mentionPopupProps, skillEditorAriaAttributes, skillPopupProps]);

  const composerPlaceholder = isWorkspaceAgentSlashSkillsEnabled
    ? COMPOSER_PLACEHOLDER_WITH_SKILLS
    : COMPOSER_PLACEHOLDER;
  const composerPlaceholderRef = useRef(composerPlaceholder);
  composerPlaceholderRef.current = composerPlaceholder;

  /**
   * Stable so `useEditor` need not rebind when the picker mounts. Typed off the popup because this
   * module's `KeyboardEvent` is React's synthetic one.
   */
  const handleMentionPopupKeyDown = useCallback(
    (event: Parameters<WorkspaceAgentMentionPopupRef['onKeyDown']>[0]) =>
      mentionPopupRef.current?.onKeyDown(event) ?? false,
    [mentionPopupRef],
  );

  const handleSkillPopupKeyDown = useCallback(
    (event: Parameters<WorkspaceAgentSkillTriggerMenuPopupRef['onKeyDown']>[0]) =>
      skillPopupRef.current?.onKeyDown(event) ?? false,
    [skillPopupRef],
  );

  const composerEditorRef = useRef<Editor | null>(null);
  const composerWorkspaceIdRef = useRef(session.workspaceId);
  const referenceUrlPasteInFlightRef = useRef<boolean>(false);
  composerWorkspaceIdRef.current = session.workspaceId;

  /**
   * Live ARIA + keydown live on this object. `useEditor` re-applies `options.current` every render
   * (shallow `editorProps` identity); a competing `setOptions` would lose `aria-expanded` on the
   * next composer render after the picker opened.
   */
  const clipboardEditorProps = useMemo(
    () => ({
      clipboardTextParser: parseWorkspaceAgentComposerClipboardText,
      handlePaste: (view: EditorView, event: ClipboardEvent) =>
        handleWorkspaceAgentComposerPaste({
          editor: composerEditorRef.current,
          event,
          referenceUrlPasteInFlightRef,
          view,
          workspaceId: composerWorkspaceIdRef.current ?? undefined,
        }),
    }),
    [],
  );

  const editorOptions = useMemo(
    () => ({
      content: '',
      editorProps: {
        attributes: { ...WORKSPACE_AGENT_COMPOSER_EDITOR_ATTRIBUTES, ...editorAriaAttributes },
        ...clipboardEditorProps,
        handleDOMEvents: workspaceAgentComposerSelectionDropEditorDomEventHandlers,
        handleKeyDown: (view: EditorView, event: WorkspaceAgentComposerEditorKeyDownEvent) =>
          composerEditorKeyDownRef.current(view, event),
      },
      extensions: [
        StarterKit.configure({
          blockquote: false,
          bold: false,
          bulletList: false,
          code: false,
          codeBlock: false,
          heading: false,
          horizontalRule: false,
          italic: false,
          listItem: false,
          orderedList: false,
          strike: false,
        }),
        Placeholder.configure({
          // TipTap only sets `data-placeholder`; rendering it is the integrator's CSS. Overriding
          // this option replaces the default class, so `is-editor-empty` has to be kept.
          emptyEditorClass:
            'is-editor-empty before:pointer-events-none before:float-left before:h-0 before:text-gray-9 before:content-[attr(data-placeholder)]',
          placeholder: () => composerPlaceholderRef.current,
          // The first send on a new thread assigns `sessionId` before the session is in the
          // sessions list, so `isActiveSessionArchiveStatusPending` holds the editor read-only
          // until the session GET lands. The default drops every placeholder decoration while
          // the editor is read-only, which is the hint blinking off after send. The read-only
          // states a user can see (archived, credits depleted) replace the composer with a dock.
          showOnlyWhenEditable: false,
        }),
        WorkspaceAgentPillExtension,
        WorkspaceAgentMentionSuggestion.configure({ suggestion: suggestionOptions }),
        WorkspaceAgentSkillMonikerNode,
        // Always register: TipTap `useEditor` does not add extensions later when flags flip on.
        // Slash gating is runtime via suggestion `allow` and `isSlashSkillsEnabled`.
        WorkspaceAgentSkillSuggestion.configure({
          getSkillById: (skillId) => getSkillByIdRef.current(skillId),
          getSkills: () => skillsRef.current,
          hasSkillsCatalogNextPage: () => hasSkillsListNextPageRef.current,
          isSlashSkillsEnabled,
          requestSkillsCatalogNextPage: () => requestSkillsListNextPageRef.current(),
          suggestion: skillSuggestionOptions,
        }),
      ],
      immediatelyRender: false,
    }),
    [clipboardEditorProps, editorAriaAttributes, isSlashSkillsEnabled, skillSuggestionOptions, suggestionOptions],
  );

  const editor = useEditor(editorOptions);
  composerEditorRef.current = editor;

  useWorkspaceAgentSkillMonikerCatalogUpgradeSync({
    editor,
    isSlashSkillsEnabled,
    skills,
  });

  usePrivateWorkspaceAgentMarketingComposerDraftSync({
    editor,
    isCreditsDepleted: isCreditsDepleted || isActiveSessionArchiveBlocked,
    taskSubmitTypeRef,
    workspaceId: session.workspaceId,
  });

  // A subscription rather than a read during render: TipTap 3 stopped re-rendering on every
  // transaction, so `editor` alone would still describe the document as it was when the panel
  // mounted. Not `editor.isEmpty`, since a document holding only spaces has nothing to send.
  const hasSendableContent = useEditorState({
    editor,
    selector: selectHasSendableWorkspaceAgentComposerContent,
  });

  // Scope pills live in selection state, not the doc, so the selector cannot see them. Board scope
  // rides along in metadata but does not make an empty message sendable (WORK-518).
  const hasOutgoingPayload = !!hasSendableContent || hasExplicitSelection;
  const isElevatedAboveOnboarding = useWorkspaceAgentPanelStore((state) => state.isElevatedAboveOnboarding);
  // Destructured so the callbacks below depend on the fields they use, not the whole session api.
  const { getActiveSessionId, isComposerRunLocked, sendMessage, sessionId } = session;
  const canSubmit =
    hasOutgoingPayload &&
    !isComposerRunLocked &&
    !isComposerLocked &&
    !isCreditsDepleted &&
    !isActiveSessionArchiveBlocked &&
    !isElevatedAboveOnboarding;
  const isStopLoading = session.isCancelling;
  const isStopEnabled = isComposerRunLocked && session.sessionId !== null && !isStopLoading && !session.isSending;
  const composerStopTooltip = useMemo(
    () => getWorkspaceAgentComposerStopTooltip({ isStopEnabled, isStopLoading }),
    [isStopEnabled, isStopLoading],
  );
  const composerRunStatusMessage = useMemo(() => {
    if (!isComposerRunLocked) {
      return '';
    }

    if (composerStopTooltip) {
      return composerStopTooltip;
    }

    return 'Agent is running.';
  }, [isComposerRunLocked, composerStopTooltip]);

  composerEditorKeyDownRef.current = (view: EditorView, event: WorkspaceAgentComposerEditorKeyDownEvent) =>
    handleWorkspaceAgentComposerEditorKeyDown({
      canSubmit,
      event,
      onMentionPopupKeyDown: handleMentionPopupKeyDown,
      onSkillPopupKeyDown: handleSkillPopupKeyDown,
      onSubmit: () => handleSubmitRef.current(),
      view,
    });

  const clearWorkspaceSelection = useCallback(() => {
    if (selectAll) {
      resetSelectAll();
    }

    dispatch(resetSelectedItemsAction());
  }, [dispatch, resetSelectAll, selectAll]);

  const snapshotWorkspaceSelection = useCallback((): SubmittedWorkspaceSelectionSnapshot => {
    const selectAllState = useViewsSelectAllStore.getState();

    return {
      includeNestedItems: selectAllState.includeNestedItems,
      selectAll: selectAllState.selectAll,
      selectAllRootBoardId: selectAllState.selectAllRootBoardId,
      selectedItems: selectedItemsSelector(store.getState()),
    };
  }, [store]);

  const restoreWorkspaceSelection = useCallback(
    (snapshot: SubmittedWorkspaceSelectionSnapshot) => {
      useViewsSelectAllStore.setState({
        includeNestedItems: snapshot.includeNestedItems,
        selectAll: snapshot.selectAll,
        selectAllRootBoardId: snapshot.selectAllRootBoardId,
      });
      dispatch(hydrateSelectedItemsAction({ selectedItems: snapshot.selectedItems }));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(() => {
    if (!editor || isComposerRunLocked || isComposerLocked) {
      return;
    }

    // The send button stays off while the scripted demo is on the sky. The handoff still posts
    // its hero prompt in that same beat, before the sky has finished leaving.
    if (isElevatedAboveOnboarding && taskSubmitTypeRef.current !== 'hero_chip') {
      return;
    }

    if (blockIfCreditsDepleted() || isActiveSessionArchiveBlocked) {
      return;
    }

    const { content, pills: editorPills } = serializeWorkspaceAgentComposerContent(editor, composerSerializeOptions);

    // Scope pills live in selection state, not the doc. On an id collision the editor pill wins: its
    // moniker in `content` points at it.
    const outgoingPillsById = new Map<string, AgentInboundMessagePill>();

    for (const pill of buildWorkspaceAgentComposerOutboundPills(scopePills)) {
      outgoingPillsById.set(pill.id, pill);
    }

    for (const pill of editorPills) {
      outgoingPillsById.set(pill.id, pill);
    }

    const outgoingPills = normalizeWorkspaceAgentOutboundPills(
      [...outgoingPillsById.values()],
      new Set(editorPills.map((pill) => pill.id)),
    );
    const {
      artifacts,
      handles,
      optimisticPills,
      pills: wirePills,
    } = splitWorkspaceAgentOutboundListPills(outgoingPills);

    // Mirrors `canSubmit`: editor content or explicit asset selection arms send. Board scope rides
    // along in metadata but must not post an empty message on its own (WORK-518).
    const hasSendableEditorContent = hasSendableWorkspaceAgentComposerContent(editor, composerSerializeOptions);

    if (!hasSendableEditorContent && !hasExplicitSelection) {
      return;
    }

    // The select-assets prompt stays up until the thread id changes. A send in the same thread
    // has to close it, or the prompt stays pinned under later turns.
    closeGuidedVariantFlow();

    useWorkspaceAgentPanelStore.getState().clearMarketingComposerDraft();

    // Kept so a send that never reaches the server can hand the message back, rather than clearing
    // what the user typed into nothing.
    const submittedDoc = editor.getJSON();
    const submittedSessionId = sessionId;
    const submitType = taskSubmitTypeRef.current;
    const submitActionsMenuTaskId = submitType === 'actions_menu' ? actionsMenuTaskIdRef.current : undefined;
    const submitGuidedFlowSubmission = guidedFlowSubmissionRef.current;
    const { skillIds: submittedSkillIds } = collectWorkspaceAgentComposerSkillIds(editor);
    let threadCreatedLocation: WorkspaceAgentThreadCreatedLocation | null = null;

    if (submittedSessionId === null) {
      threadCreatedLocation = useWorkspaceAgentPanelStore.getState().pendingThreadCreatedLocation;
    }
    const submittedSelection = snapshotWorkspaceSelection();
    const uploadedBatchOffer = useUploadedBatchOfferStore.getState();
    // Composer scope for this message only. A failed send puts the batch back with the selection.
    const submittedUploadedBatch = {
      attachedBatch: uploadedBatchOffer.attachedBatch,
      attachments: uploadedBatchOffer.attachments.slice(),
    };

    // Focus is returned explicitly because sending from the button would otherwise leave the caret
    // out of the composer, and the next message is almost always typed straight away.
    clearWorkspaceSelection();
    uploadedBatchOffer.clearAttachedBatch();
    editor.chain().clearContent().focus().run();

    const pendingStructuredSend = pendingStructuredSendRef.current;
    pendingStructuredSendRef.current = undefined;

    const structuredDisplay =
      pendingStructuredSend?.contentSnapshot === content ? pendingStructuredSend.structuredDisplay : null;

    void sendMessage({
      ...(artifacts.length > 0 ? { artifacts } : {}),
      content,
      metadata: buildWorkspaceAgentOutboundMetadata({
        artifacts: handles,
        visualContext,
        pills: wirePills,
        structuredDisplay,
      }),
      ...(optimisticPills.length > 0 ? { optimisticPills } : {}),
    }).then((result) => {
      if (result.didSend) {
        if (threadCreatedLocation) {
          const panelStore = useWorkspaceAgentPanelStore.getState();

          if (panelStore.pendingThreadCreatedLocation === threadCreatedLocation) {
            panelStore.consumePendingThreadCreatedLocation();
          }

          trackAgentThreadOpened({
            is_first_run_walkthrough: false,
            session_id: result.sessionId,
            type: 'new',
            location: threadCreatedLocation,
          });
        }

        if (submitType === 'guided_flow' && submitGuidedFlowSubmission) {
          trackAgentTaskSubmitted(
            buildAgentTaskSubmittedParams({
              guidedFlowSubmission: submitGuidedFlowSubmission,
              sessionId: result.sessionId,
              skillIds: submittedSkillIds,
              type: 'guided_flow',
            }),
          );
        } else if (submitType === 'guided_flow') {
          reportErrorToBugsnag({
            context: 'Workspace agent guided flow submission missing at send',
            error: new Error('Guided flow task was sent without submission params'),
          });
        } else {
          trackAgentTaskSubmitted(
            buildAgentTaskSubmittedParams({
              actionsMenuTaskId: submitActionsMenuTaskId,
              sessionId: result.sessionId,
              skillIds: submittedSkillIds,
              type: submitType,
            }),
          );
        }
      }

      // Only into a composer the user has left alone. Having started the next message already,
      // they would rather keep it than have the failed one put back over the top. And only into one
      // still on screen: minimizing the panel, or switching workspace, destroys the editor while the
      // send is in flight, and dispatching into a destroyed editor throws.
      //
      // The conversation has to be the one it was addressed to as well. Handed back after a session
      // switch, the next Enter would send it somewhere it was never meant for.
      const willRestore =
        !result.didSend && !editor.isDestroyed && editor.isEmpty && getActiveSessionId() === submittedSessionId;

      // A failed guided-flow send restores the seeded prompt, so the retry stays guided_flow.
      // Keep those refs only when the prompt actually comes back. Otherwise the next manual send
      // would be reported as the wizard's edit.
      if (!willRestore || submitType !== 'guided_flow') {
        taskSubmitTypeRef.current = 'manual_submit';
        actionsMenuTaskIdRef.current = undefined;
        guidedFlowSubmissionRef.current = undefined;
      }

      if (willRestore) {
        if (structuredDisplay) {
          pendingStructuredSendRef.current = {
            contentSnapshot: content,
            structuredDisplay,
          };
        }

        restoreWorkspaceSelection(submittedSelection);
        useUploadedBatchOfferStore.getState().restoreAttachedBatch(submittedUploadedBatch);
        editor.chain().setContent(submittedDoc).focus('end').run();
      }
    });
  }, [
    clearWorkspaceSelection,
    editor,
    getActiveSessionId,
    hasExplicitSelection,
    isComposerLocked,
    isComposerRunLocked,
    isElevatedAboveOnboarding,
    scopePills,
    restoreWorkspaceSelection,
    sendMessage,
    sessionId,
    snapshotWorkspaceSelection,
    blockIfCreditsDepleted,
    closeGuidedVariantFlow,
    isActiveSessionArchiveBlocked,
    trackAgentTaskSubmitted,
    trackAgentThreadOpened,
    composerSerializeOptions,
    visualContext,
  ]);

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  const handleStop = useCallback(() => {
    if (!isStopEnabled) {
      return;
    }

    void session.cancelRun();
  }, [isStopEnabled, session]);

  const handleStopMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  // `aria-disabled` (not `disabled`) keeps the control in the tab order, so the run-lock guard lives here.
  const handleAddReference = useCallback(() => {
    if (isComposerRunLocked || isComposerLocked) {
      return;
    }

    if (blockIfCreditsDepleted() || isActiveSessionArchiveBlocked) {
      return;
    }

    openManualPicker(editor);
  }, [
    blockIfCreditsDepleted,
    editor,
    isActiveSessionArchiveBlocked,
    isComposerLocked,
    isComposerRunLocked,
    openManualPicker,
  ]);

  const handleInsertSkillFromActionsMenu = useCallback(
    (skill: Skill) => {
      if (isComposerRunLocked || isComposerLocked) {
        return;
      }

      if (blockIfCreditsDepleted() || isActiveSessionArchiveBlocked) {
        return;
      }

      insertWorkspaceAgentSkillMonikerInEditor(editor, skill, {
        onInserted: (insertedSkill) => handleComposerSkillMonikerInserted(insertedSkill, 'actions_menu'),
      });
    },
    [
      blockIfCreditsDepleted,
      editor,
      handleComposerSkillMonikerInserted,
      isActiveSessionArchiveBlocked,
      isComposerLocked,
      isComposerRunLocked,
    ],
  );

  const handleSendKeyDown = useMemo(() => blockActivationWhen(!canSubmit), [canSubmit]);

  const handleStopKeyDown = useMemo(() => blockActivationWhen(!isStopEnabled), [isStopEnabled]);

  const refocusEditor = useCallback(() => {
    if (isComposerLocked || !editor || editor.isDestroyed) {
      return;
    }

    editor.view.focus();
  }, [editor, isComposerLocked]);

  useEffect(() => {
    if (previousSessionIdForPendingStructuredSendRef.current === sessionId) {
      return;
    }

    previousSessionIdForPendingStructuredSendRef.current = sessionId;
    pendingStructuredSendRef.current = undefined;
  }, [sessionId]);

  useEffect(() => {
    const previousComposerRunLocked = previousComposerRunLockedRef.current;
    previousComposerRunLockedRef.current = isComposerRunLocked;

    if (
      previousComposerRunLocked === true &&
      !isComposerRunLocked &&
      !isComposerLocked &&
      editor &&
      !editor.isDestroyed
    ) {
      editor.view.focus();
    }
  }, [editor, isComposerLocked, isComposerRunLocked]);

  const prevSessionIdForSubmitTypeRef = useRef(sessionId);

  useEffect(() => {
    if (prevSessionIdForSubmitTypeRef.current === sessionId) {
      return;
    }

    prevSessionIdForSubmitTypeRef.current = sessionId;
    taskSubmitTypeRef.current = 'manual_submit';
    actionsMenuTaskIdRef.current = undefined;
    guidedFlowSubmissionRef.current = undefined;
  }, [sessionId]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    const isEditable = !isCreditsDepleted && !isActiveSessionArchiveBlocked && !isComposerLocked;

    editor.setEditable(isEditable);

    if (isComposerLocked && editor.isFocused) {
      editor.commands.blur();
    }
  }, [editor, isActiveSessionArchiveBlocked, isComposerLocked, isCreditsDepleted]);

  useEffect(() => {
    if (!editor || !seed) {
      return;
    }

    // A seed that lands while the wizard holds the lock must wait. Clearing it here would drop
    // the send. The guided-variant path unlocks in the same turn it seeds, so this effect runs
    // again with the lock released and still posts the prompt.
    if (isComposerLocked) {
      return;
    }

    if (
      isCreditsDepleted ||
      isActiveSessionArchiveBlocked ||
      (seed.workspaceId && seed.workspaceId !== session.workspaceId)
    ) {
      useWorkspaceAgentPanelStore.getState().clearOnboardingThreadCreatedLocation();
      clearSeed();

      return;
    }

    guidedFlowSubmissionRef.current = undefined;

    if (seed.source === 'hero_chip') {
      taskSubmitTypeRef.current = 'hero_chip';
    } else if (seed.source === 'guided_flow') {
      taskSubmitTypeRef.current = 'guided_flow';
      guidedFlowSubmissionRef.current = seed.guidedFlowSubmission;
    } else if (seed.source === 'selection_menu') {
      taskSubmitTypeRef.current = 'selection_menu';
    } else if (seed.source === 'edit_tags_modal') {
      taskSubmitTypeRef.current = 'edit_tags_modal';
    } else if (seed.source === 'marketing_url') {
      taskSubmitTypeRef.current = 'marketing_url';
    } else if (seed.source === 'actions_menu') {
      taskSubmitTypeRef.current = 'actions_menu';
      actionsMenuTaskIdRef.current = seed.actionsMenuTaskId;
    } else if (seed.source === 'announcement') {
      taskSubmitTypeRef.current = 'announcement';
    } else if (seed.source === 'board_menu') {
      taskSubmitTypeRef.current = 'board_menu';
    }

    const jsonContent =
      seed.jsonContent ??
      buildWorkspaceAgentComposerSeedContent({
        boardPill: seed.boardPill
          ? { boardId: seed.boardPill.boardId, label: seed.boardPill.title ?? 'Board', pillId: uuidv4() }
          : undefined,
        prompt: seed.prompt,
      });

    if (seed.sendImmediately || seed.source === 'board_menu') {
      // The posted turn is only the seeded prompt. A draft already in the composer must not ride along.
      editor.chain().clearContent().focus('end').run();
    }

    editor.chain().focus('end').insertContent(jsonContent).run();

    if (seed.sendImmediately) {
      // An intro seed that lands in a thread already on screen is not a new thread.
      // A New thread click that already replaced `onboarding` is left alone.
      if (session.sessionId !== null) {
        useWorkspaceAgentPanelStore.getState().clearOnboardingThreadCreatedLocation();
      }

      if (seed.structuredDisplay) {
        const { content: contentSnapshot } = serializeWorkspaceAgentComposerContent(editor, composerSerializeOptions);

        pendingStructuredSendRef.current = {
          contentSnapshot,
          structuredDisplay: seed.structuredDisplay,
        };
      }

      handleSubmitRef.current();
    }

    clearSeed();
  }, [
    clearSeed,
    composerSerializeOptions,
    editor,
    isActiveSessionArchiveBlocked,
    isComposerLocked,
    isCreditsDepleted,
    seed,
    session.sessionId,
    session.workspaceId,
  ]);

  // Opening the panel destroys the FAB that was focused, so the caret is placed here rather than
  // left on `<body>`.
  useEffect(() => {
    if (!editor || pendingFocus !== 'composer' || isComposerLocked) {
      return;
    }

    editor.commands.focus('end');
    clearPendingFocus();
  }, [clearPendingFocus, editor, isComposerLocked, pendingFocus]);

  return {
    canSubmit,
    composerPlaceholder,
    composerRunStatusMessage,
    composerStopTooltip,
    editor,
    handleAddReference,
    handleInsertSkillFromActionsMenu,
    handleSendKeyDown,
    handleStop,
    handleStopKeyDown,
    handleStopMouseDown,
    handleSubmit,
    isComposerLocked,
    isComposerRunLocked,
    isStopEnabled,
    isStopLoading,
    mentionPopupProps,
    mentionPopupRef,
    refocusEditor,
    skillPopupProps,
    skillPopupRef,
  };
};
